import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  subscriptionsApi,
  type BillingCompany,
  type BillingHistoryItem,
} from "@/lib/api";
import { PaymentModal } from "@/components/PaymentModal";
import { CustomerPortalModal } from "@/components/billing/CustomerPortalModal";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
  Building2,
  CreditCard,
  Download,
  ExternalLink,
  FileText,
  RefreshCw,
  XCircle,
  CheckCircle2,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { PageMeta, DASHBOARD_PAGE_META } from "@/lib/seo";

// ═══════════════════════════════════════════════════════════════════
// Status helpers
// ═══════════════════════════════════════════════════════════════════

const billingStatusConfig: Record<
  string,
  { label: string; description: string; style: string; icon: typeof CheckCircle2 }
> = {
  active_subscription: {
    label: "Aktiv prenumeration",
    description: "Förnyas automatiskt",
    style: "bg-green-100 text-green-700 border-green-200",
    icon: CheckCircle2,
  },
  canceled_subscription: {
    label: "Avslutas",
    description: "Aktiv till periodens slut",
    style: "bg-yellow-100 text-yellow-700 border-yellow-200",
    icon: Clock,
  },
  past_due: {
    label: "Betalning förfallen",
    description: "Uppdatera betalningsmetod",
    style: "bg-red-100 text-red-700 border-red-200",
    icon: AlertTriangle,
  },
  inactive: {
    label: "Ej aktiverat",
    description: "Aktivera för att använda alla funktioner",
    style: "bg-gray-100 text-gray-500 border-gray-200",
    icon: XCircle,
  },
};

export function Billing() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<BillingCompany[]>([]);
  const [history, setHistory] = useState<BillingHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  const [_portalLoading, setPortalLoading] = useState(false);
  void _portalLoading; // Setter used for loading state
  const [showPortalModal, setShowPortalModal] = useState(false);
  const [activatingCompany, setActivatingCompany] =
    useState<BillingCompany | null>(null);

  // Track if user navigated to portal (for refresh on return)
  const wentToPortal = useRef(false);

  // Check if user has any active subscriptions
  const hasActiveSubscription = companies.some(
    (c) => c.billingStatus === "active_subscription" || c.billingStatus === "canceled_subscription"
  );

  useEffect(() => {
    loadBilling();
    loadHistory();
  }, []);

  // Refresh data when user returns from Stripe portal
  useEffect(() => {
    const handleFocus = () => {
      if (wentToPortal.current) {
        wentToPortal.current = false;
        // Small delay to ensure Stripe has processed any changes
        setTimeout(() => {
          loadBilling();
          loadHistory();
        }, 500);
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  const loadBilling = async () => {
    setIsLoading(true);
    try {
      const response = await subscriptionsApi.getBilling();
      if (response.success && response.data) {
        setCompanies(response.data.companies);
      } else {
        toast.error(response.error?.message || "Kunde inte ladda betalningsinformation");
      }
    } catch {
      toast.error("Kunde inte ladda betalningsinformation");
    } finally {
      setIsLoading(false);
    }
  };

  const loadHistory = async () => {
    setIsHistoryLoading(true);
    try {
      const response = await subscriptionsApi.getBillingHistory();
      if (response.success && response.data) {
        setHistory(response.data.items);
      }
    } catch {
      toast.error("Kunde inte ladda betalningshistorik");
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const handleCancel = useCallback(
    async (companyId: string) => {
      if (
        !confirm(
          "Är du säker på att du vill avsluta prenumerationen? Den förblir aktiv till periodens slut."
        )
      )
        return;
      try {
        const response = await subscriptionsApi.cancel(companyId);
        if (response.success) {
          toast.success("Prenumerationen avslutas vid periodens slut");
          loadBilling();
        } else {
          toast.error(response.error?.message || "Kunde inte avsluta prenumerationen");
        }
      } catch {
        toast.error("Ett fel uppstod");
      }
    },
    []
  );

  const handleResume = useCallback(
    async (companyId: string) => {
      try {
        const response = await subscriptionsApi.resume(companyId);
        if (response.success) {
          toast.success("Prenumerationen har återupptagits");
          loadBilling();
        } else {
          toast.error(response.error?.message || "Kunde inte återuppta prenumerationen");
        }
      } catch {
        toast.error("Ett fel uppstod");
      }
    },
    []
  );

  const handleActivate = useCallback(
    (company: BillingCompany) => {
      setActivatingCompany(company);
    },
    []
  );

  const openCustomerPortal = useCallback(async () => {
    setPortalLoading(true);
    try {
      const response = await subscriptionsApi.customerPortal();
      if (response.success && response.data) {
        wentToPortal.current = true;
        window.location.href = response.data.portalUrl;
      } else {
        toast.error(
          response.error?.message || "Kunde inte öppna kundportalen"
        );
        setShowPortalModal(false);
      }
    } catch {
      toast.error("Ett fel uppstod");
      setShowPortalModal(false);
    } finally {
      setPortalLoading(false);
    }
  }, []);

  const handleCustomerPortalClick = useCallback(() => {
    setShowPortalModal(true);
  }, []);

  const handleDownloadReceipt = useCallback(
    async (item: BillingHistoryItem) => {
      const result = await subscriptionsApi.downloadReceipt(item.id);

      if (result.success && result.blob) {
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = result.filename || `kvitto-${item.id}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Kvitto nedladdat");
      } else {
        toast.error(result.error || "Kunde inte ladda ner kvitto");
      }
    },
    []
  );

  return (
    <>
      <PageMeta
        title={DASHBOARD_PAGE_META.billing.title}
        description={DASHBOARD_PAGE_META.billing.description}
        noIndex
      />
      <div className="space-y-8">
        {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Betalningar</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
            Hantera prenumerationer och betalningar
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleCustomerPortalClick}
          className="w-full sm:w-auto flex-shrink-0"
        >
          <ExternalLink size={16} />
          <span className="truncate">Hantera betalningsmetod</span>
        </Button>
      </div>

      {/* Company Cards */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Dina företag
        </h2>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : companies.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <Building2
              size={40}
              className="mx-auto text-gray-300 mb-3"
            />
            <p className="text-gray-500">
              Du har inga företag. Lägg till ett företag för att komma igång.
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate("/dashboard/companies")}
            >
              Gå till mina företag
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {companies.map((company) => (
              <CompanyBillingCard
                key={company.companyId}
                company={company}
                onCancel={handleCancel}
                onResume={handleResume}
                onActivate={handleActivate}
              />
            ))}
          </div>
        )}
      </section>

      {/* Payment History */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Betalningshistorik
        </h2>

        {isHistoryLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : history.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <FileText
              size={40}
              className="mx-auto text-gray-300 mb-3"
            />
            <p className="text-gray-500">Ingen betalningshistorik finns.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      Datum
                    </th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      Företag
                    </th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 hidden sm:table-cell">
                      Beskrivning
                    </th>
                    <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      Belopp
                    </th>
                    <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      Kvitto
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {history.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {new Date(item.date).toLocaleDateString("sv-SE")}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {item.companyName}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">
                        {item.description}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 whitespace-nowrap">
                        {item.amount
                          ? `${new Intl.NumberFormat("sv-SE").format(parseFloat(item.amount))} kr`
                          : "-"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleDownloadReceipt(item)}
                          className="text-primary-600 hover:text-primary-700 transition-colors"
                          title="Ladda ner kvitto"
                        >
                          <Download size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {activatingCompany && (
        <PaymentModal
          isOpen={!!activatingCompany}
          onClose={() => setActivatingCompany(null)}
          companyName={activatingCompany.companyName}
          companyId={activatingCompany.companyId}
          onActivated={() => {
            setActivatingCompany(null);
            loadBilling();
          }}
        />
      )}

      <CustomerPortalModal
        isOpen={showPortalModal}
        onClose={() => setShowPortalModal(false)}
        onConfirm={openCustomerPortal}
        hasActiveSubscription={hasActiveSubscription}
      />
    </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Company Billing Card
// ═══════════════════════════════════════════════════════════════════

function CompanyBillingCard({
  company,
  onCancel,
  onResume,
  onActivate,
}: {
  company: BillingCompany;
  onCancel: (companyId: string) => void;
  onResume: (companyId: string) => void;
  onActivate: (company: BillingCompany) => void;
}) {
  const config =
    billingStatusConfig[company.billingStatus] ||
    billingStatusConfig.inactive;
  const StatusIcon = config.icon;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col">
      {/* Company info */}
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 bg-gray-100 rounded-lg">
          <Building2 size={20} className="text-gray-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">
            {company.companyName}
          </h3>
          <p className="text-xs text-gray-500">{company.organizationNumber}</p>
        </div>
      </div>

      {/* Status badge */}
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${config.style} mb-4`}
      >
        <StatusIcon size={16} />
        <div>
          <p className="text-sm font-medium">{config.label}</p>
          <p className="text-xs opacity-80">{config.description}</p>
        </div>
      </div>

      {/* Subscription period info */}
      {company.subscription && (
        <div className="text-xs text-gray-500 mb-4 space-y-1">
          {company.billingStatus === "active_subscription" &&
            company.subscription.currentPeriodEnd && (
              <p>
                Nästa förnyelse:{" "}
                <span className="font-medium text-gray-700">
                  {new Date(
                    company.subscription.currentPeriodEnd
                  ).toLocaleDateString("sv-SE")}
                </span>
              </p>
            )}
          {company.billingStatus === "canceled_subscription" &&
            company.subscription.currentPeriodEnd && (
              <p>
                Aktiv till:{" "}
                <span className="font-medium text-gray-700">
                  {new Date(
                    company.subscription.currentPeriodEnd
                  ).toLocaleDateString("sv-SE")}
                </span>
              </p>
            )}
        </div>
      )}

      {/* Actions */}
      <div className="mt-auto pt-2">
        {company.billingStatus === "active_subscription" && (
          <button
            onClick={() => onCancel(company.companyId)}
            className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
          >
            Avsluta prenumeration
          </button>
        )}
        {company.billingStatus === "canceled_subscription" && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onResume(company.companyId)}
          >
            <RefreshCw size={14} />
            Återuppta
          </Button>
        )}
        {company.billingStatus === "past_due" && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onActivate(company)}
          >
            <CreditCard size={14} />
            Uppdatera betalning
          </Button>
        )}
        {company.billingStatus === "inactive" && (
          <Button
            size="sm"
            onClick={() => onActivate(company)}
          >
            Aktivera företag
          </Button>
        )}
      </div>
    </div>
  );
}
